import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import EventsList from "../EventsList/EventsList";
import ChatsList from "../ChatsList/ChatsList";
import styles from "./Dashboard.module.css";

const ACTIONS_STORAGE_KEY = "dashboardActionOrder";
const DASHBOARD_TAB_STORAGE_KEY = "dashboardActiveTab";
const DEBT_SORT_STORAGE_KEY = "dashboardDebtSortMode";

const EVENTS_CACHE_KEY = "eventsData";
const EVENTS_CACHE_TIME_KEY = "eventsDataTimestamp";
const EVENTS_CACHE_TTL = 5 * 60 * 1000;

const buildDebtListFromEvents = (allEvents) => {
  return allEvents.filter(
    (item) =>
      Number(item?.debt || 0) > 0 &&
      item?.dateOfPay &&
      (item?.client || item?.clientBrand || item?.name)
  );
};

const readCachedEventsSync = () => {
  try {
    const raw = localStorage.getItem(EVENTS_CACHE_KEY);
    const rawTimestamp = localStorage.getItem(EVENTS_CACHE_TIME_KEY);

    if (!raw || !rawTimestamp) return null;

    const timestamp = Number(rawTimestamp);
    const isFresh = Date.now() - timestamp < EVENTS_CACHE_TTL;

    if (!isFresh) return null;

    const parsed = JSON.parse(raw);

    return {
      current: parsed?.current || [],
      future: parsed?.future || [],
      past: parsed?.past || [],
    };
  } catch {
    return null;
  }
};

const buildEventsCachePayload = (current, future, past) => ({
  current,
  future,
  past,
});

const getDashboardUserId = (currentUser) => {
  const userId =
    currentUser?.id ||
    currentUser?.userId ||
    currentUser?.user_id ||
    currentUser?.employeeId ||
    currentUser?.uid ||
    currentUser?.guid ||
    currentUser?.uuid ||
    "";

  return userId ? String(userId) : "";
};

const collectVisibleEventIds = (...eventGroups) => {
  return Array.from(
    new Set(
      eventGroups
        .flat()
        .map((event) => String(event?.id || "").trim())
        .filter(Boolean)
    )
  );
};

function Dashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const dashboardUserId = useMemo(
    () => getDashboardUserId(currentUser),
    [currentUser]
  );

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem(DASHBOARD_TAB_STORAGE_KEY) || "main";
  });

  const [debtSortMode, setDebtSortMode] = useState(() => {
    return localStorage.getItem(DEBT_SORT_STORAGE_KEY) || "date";
  });

  const [debtSearchTerm, setDebtSearchTerm] = useState("");

  const [cachedEventsAtStart] = useState(() => readCachedEventsSync());

  const initialDebtItems = cachedEventsAtStart
    ? buildDebtListFromEvents([
        ...cachedEventsAtStart.current,
        ...cachedEventsAtStart.future,
        ...cachedEventsAtStart.past,
      ])
    : [];

  const [debtItems, setDebtItems] = useState(initialDebtItems);
  const [debtLoading, setDebtLoading] = useState(!cachedEventsAtStart);
  const [debtError, setDebtError] = useState(null);

  const rights = useMemo(() => {
    if (currentUser?.rights && typeof currentUser.rights === "object") {
      return currentUser.rights;
    }

    try {
      return JSON.parse(localStorage.getItem("userRights") || "{}");
    } catch {
      return {};
    }
  }, [currentUser]);
  const canViewChats = true;

  useEffect(() => {
    localStorage.setItem(DASHBOARD_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem(DEBT_SORT_STORAGE_KEY, debtSortMode);
  }, [debtSortMode]);

  useEffect(() => {
    let mounted = true;
    const hasFreshCache = !!cachedEventsAtStart;

    const normalizeGrouped = (data) => {
      const grouped = data?.grouped || {
        past:
          data?.past ||
          data?.events?.filter((e) => e.relevance === "past") ||
          [],
        current:
          data?.current ||
          data?.events?.filter((e) => e.relevance === "current") ||
          [],
        future:
          data?.future ||
          data?.events?.filter((e) => e.relevance === "future") ||
          [],
      };

      return {
        past: grouped.past || [],
        current: grouped.current || [],
        future: grouped.future || [],
      };
    };

    const saveEventsToCache = (current, future, past) => {
      const payload = buildEventsCachePayload(current, future, past);
      localStorage.setItem(EVENTS_CACHE_KEY, JSON.stringify(payload));
      localStorage.setItem(EVENTS_CACHE_TIME_KEY, String(Date.now()));
    };

    const syncChatParticipants = async (current, future, past) => {
      if (!dashboardUserId) {
        console.warn(
          "Не удалось синхронизировать чаты: отсутствует ID пользователя"
        );
        return;
      }

      const eventIds = collectVisibleEventIds(current, future, past);

      try {
        const response = await fetch("/api/events/chats/participants/sync", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            userId: dashboardUserId,
            eventIds,
          }),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            data?.message || `Ошибка синхронизации чатов: ${response.status}`
          );
        }

        console.log("Участники чатов синхронизированы:", data);
      } catch (error) {
        /*
         * Ошибка участников не должна ломать загрузку Dashboard.
         */
        console.error("Ошибка синхронизации участников чатов:", error);
      }
    };

    const fetchDebtData = async () => {
      try {
        setDebtLoading(!hasFreshCache);
        setDebtError(null);

        const activeResponse = await fetch("/api/events/active-list", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (!mounted) return;

        if (activeResponse.status === 401) {
          navigate("/login");
          return;
        }

        const activeData = await activeResponse.json().catch(() => null);

        if (!activeResponse.ok) {
          throw new Error(
            activeData?.message || "Ошибка загрузки актуальных мероприятий"
          );
        }

        const activeGrouped = normalizeGrouped(activeData);
        const current = activeGrouped.current || [];
        const future = activeGrouped.future || [];
        const pastFromCache = cachedEventsAtStart?.past || [];

        setDebtItems(
          buildDebtListFromEvents([...current, ...future, ...pastFromCache])
        );
        setDebtLoading(false);

        saveEventsToCache(current, future, pastFromCache);
        syncChatParticipants(current, future, pastFromCache);

        const pastResponse = await fetch("/api/events/past-list", {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });

        if (!mounted) return;

        if (pastResponse.status === 401) {
          navigate("/login");
          return;
        }

        const pastData = await pastResponse.json().catch(() => null);

        if (!pastResponse.ok) {
          throw new Error(
            pastData?.message || "Ошибка загрузки прошлых мероприятий"
          );
        }

        const pastGrouped = normalizeGrouped(pastData);
        const past = pastGrouped.past || [];

        setDebtItems(buildDebtListFromEvents([...current, ...future, ...past]));
        saveEventsToCache(current, future, past);
        syncChatParticipants(current, future, past);
      } catch (error) {
        if (!mounted) return;
        setDebtError(error?.message || "Ошибка загрузки задолженностей");
        setDebtLoading(false);
      }
    };

    fetchDebtData();

    return () => {
      mounted = false;
    };
  }, [navigate, cachedEventsAtStart, dashboardUserId]);

  const rememberActionUsage = (actionId) => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(ACTIONS_STORAGE_KEY) || "[]"
      );
      const normalized = Array.isArray(saved) ? saved : [];
      const nextOrder = [
        actionId,
        ...normalized.filter((id) => id !== actionId),
      ];
      localStorage.setItem(ACTIONS_STORAGE_KEY, JSON.stringify(nextOrder));
    } catch {
      localStorage.setItem(ACTIONS_STORAGE_KEY, JSON.stringify([actionId]));
    }
  };

  const actionHandlers = {
    lead: () => navigate("/lead-form"),
    order: () => navigate("/order-form"),
    engineer: () => navigate("/engineer-form"),
    dispatcher: () => navigate("/request-assignment"),
    night: () => navigate("/night-form"),
    coordinates: () => navigate("/coordinates-form"),
    photo: () => navigate("/photo-form"),
  };

  const availableActions = useMemo(() => {
    const actions = [];

    if (rights?.newLead) {
      actions.push({
        id: "lead",
        label: "Создать лид",
      });
    }

    if (rights?.orderAuto) {
      actions.push({
        id: "order",
        label: "Заказать машину",
      });
    }

    if (rights?.recordToEngineer) {
      actions.push({
        id: "engineer",
        label: "Запись к инженеру",
      });
    }

    if (rights?.dispatcher) {
      actions.push({
        id: "dispatcher",
        label: "Форма диспетчера",
      });
    }

    if (rights?.eventFromNightWork || rights?.recordToEngineer) {
      actions.push({
        id: "night",
        label: "Оформить отгул",
      });
    }

    if (rights?.newLd) {
      actions.push({
        id: "coordinates",
        label: "Отправить координаты",
      });
    }

    if (rights?.photoUpload) {
      actions.push({
        id: "photo",
        label: "Загрузка фото",
      });
    }

    let savedOrder = [];
    try {
      const parsed = JSON.parse(
        localStorage.getItem(ACTIONS_STORAGE_KEY) || "[]"
      );
      savedOrder = Array.isArray(parsed) ? parsed : [];
    } catch {
      savedOrder = [];
    }

    const orderMap = new Map(savedOrder.map((id, index) => [id, index]));

    return [...actions].sort((a, b) => {
      const aIndex = orderMap.has(a.id)
        ? orderMap.get(a.id)
        : Number.MAX_SAFE_INTEGER;
      const bIndex = orderMap.has(b.id)
        ? orderMap.get(b.id)
        : Number.MAX_SAFE_INTEGER;

      return aIndex - bIndex;
    });
  }, [rights]);

  const sortedDebtItems = useMemo(() => {
    const items = [...debtItems];

    if (debtSortMode === "amount") {
      return items.sort((a, b) => Number(b.debt || 0) - Number(a.debt || 0));
    }

    return items.sort((a, b) => {
      const dateA = new Date(a.dateOfPay || 0).getTime();
      const dateB = new Date(b.dateOfPay || 0).getTime();
      return dateA - dateB;
    });
  }, [debtItems, debtSortMode]);

  const filteredDebtItems = useMemo(() => {
    const q = debtSearchTerm.trim().toLowerCase();
    if (!q) return sortedDebtItems;

    return sortedDebtItems.filter((item) => {
      const managerName = String(item?.managerName || "").toLowerCase();
      const id = String(item?.id || "").toLowerCase();
      const name = String(item?.name || "").toLowerCase();
      const client = String(item?.client || "").toLowerCase();

      return (
        managerName.includes(q) ||
        id.includes(q) ||
        name.includes(q) ||
        client.includes(q)
      );
    });
  }, [sortedDebtItems, debtSearchTerm]);

  const debtSummary = useMemo(() => {
    const totalCount = filteredDebtItems.length;
    const totalAmount = filteredDebtItems.reduce(
      (sum, item) => sum + Number(item.debt || 0),
      0
    );

    return {
      totalCount,
      totalAmount,
    };
  }, [filteredDebtItems]);

  const handleActionClick = (actionId) => {
    rememberActionUsage(actionId);
    const handler = actionHandlers[actionId];
    if (handler) handler();
  };

  const formatMoney = (value) => {
    const amount = Number(value || 0);
    return amount.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("ru-RU");
  };

  const isDebtOverdue = (value) => {
    if (!value) return false;

    const dueDate = new Date(value);

    if (Number.isNaN(dueDate.getTime())) {
      return false;
    }

    const today = new Date();

    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const renderMainSection = () => (
    <>
      {availableActions.length > 0 && (
        <section className={styles.actionsSection}>
          <div className={styles.sectionHeaderRow} />

          <div className={styles.actionsScroller}>
            {availableActions.map((action) => (
              <button
                key={action.id}
                className={styles.actionCard}
                onClick={() => handleActionClick(action.id)}
              >
                <span className={styles.actionLabel}>{action.label}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className={styles.debtSection}>
        <div className={styles.debtSectionHeader}>
          <div className={styles.debtSearchWrap}>
            <input
              type="text"
              placeholder="Поиск..."
              value={debtSearchTerm}
              onChange={(e) => setDebtSearchTerm(e.target.value)}
              className={styles.debtSearchInput}
            />
            {debtSearchTerm && (
              <button
                type="button"
                className={styles.debtSearchClear}
                onClick={() => setDebtSearchTerm("")}
                aria-label="Очистить поиск"
                title="Очистить"
              >
                ✕
              </button>
            )}
          </div>

          <div className={styles.debtSortToggle}>
            <button
              type="button"
              className={`${styles.sortButton} ${
                debtSortMode === "date" ? styles.sortButtonActive : ""
              }`}
              onClick={() => setDebtSortMode("date")}
            >
              По дате
            </button>

            <button
              type="button"
              className={`${styles.sortButton} ${
                debtSortMode === "amount" ? styles.sortButtonActive : ""
              }`}
              onClick={() => setDebtSortMode("amount")}
            >
              По сумме
            </button>
          </div>
        </div>

        <div className={styles.debtList}>
          {filteredDebtItems.length > 0 ? (
            filteredDebtItems.map((item) => (
              <div
                key={`${item.id}-${item.dateOfPay}`}
                className={`${styles.debtItem} ${
                  isDebtOverdue(item.dateOfPay) ? styles.debtItemOverdue : ""
                }`}
                onClick={() => navigate(`/events/${item.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    navigate(`/events/${item.id}`);
                  }
                }}
              >
                <div className={styles.debtItemTop}>
                  <span className={styles.debtClient}>{item.name}</span>
                  <span className={styles.debtAmount}>
                    {formatMoney(item.debt)}
                  </span>
                </div>

                <div className={styles.debtItemBottom}>
                  <span className={styles.debtProgram}>№ {item.id}</span>
                  <span className={styles.debtDate}>
                    Оплата до: {formatDate(item.dateOfPay)}
                  </span>
                </div>
              </div>
            ))
          ) : debtLoading ? (
            <div className={styles.debtEmpty}>Загрузка задолженностей...</div>
          ) : debtError ? (
            <div className={styles.debtEmpty}>{debtError}</div>
          ) : debtSearchTerm ? (
            <div className={styles.debtEmpty}>Ничего не найдено</div>
          ) : (
            <div className={styles.debtEmpty}>Нет данных по задолженностям</div>
          )}
        </div>

        <div className={styles.debtSummary}>
          <div className={styles.debtSummaryItem}>
            <span className={styles.debtSummaryLabel}>Всего:</span>
            <span className={styles.debtSummaryValue}>
              {debtSummary.totalCount}
            </span>
          </div>

          <div className={styles.debtSummaryItem}>
            <span className={styles.debtSummaryLabel}>Сумма:</span>
            <span className={styles.debtSummaryValue}>
              {formatMoney(debtSummary.totalAmount)}
            </span>
          </div>
        </div>
      </section>
    </>
  );

  const renderEventsSection = () => <EventsList />;
  const renderChatsSection = () => <ChatsList />;

  return (
    <div className={styles.dashboard}>
      <div className={styles.mainContent}>
        <div className={styles.desktopTabs}>
          <button
            type="button"
            className={`${styles.desktopTabButton} ${
              activeTab === "main" ? styles.desktopTabButtonActive : ""
            }`}
            onClick={() => setActiveTab("main")}
          >
            Основное
          </button>

          <button
            type="button"
            className={`${styles.desktopTabButton} ${
              activeTab === "events" ? styles.desktopTabButtonActive : ""
            }`}
            onClick={() => setActiveTab("events")}
          >
            Мероприятия
          </button>
          {canViewChats && (
            <button
              type="button"
              className={`${styles.desktopTabButton} ${
                activeTab === "third" ? styles.desktopTabButtonActive : ""
              }`}
              onClick={() => setActiveTab("third")}
            >
              Чаты
            </button>
          )}
        </div>

        {activeTab === "main" && renderMainSection()}
        {activeTab === "events" && renderEventsSection()}
        {canViewChats && activeTab === "third" && renderChatsSection()}
      </div>

      <nav className={styles.mobileBottomTabs} aria-label="Навигация Dashboard">
        <button
          type="button"
          className={`${styles.mobileTabButton} ${
            activeTab === "main" ? styles.mobileTabButtonActive : ""
          }`}
          onClick={() => setActiveTab("main")}
        >
          <span className={styles.mobileTabText}>Основное</span>
        </button>

        <button
          type="button"
          className={`${styles.mobileTabButton} ${
            activeTab === "events" ? styles.mobileTabButtonActive : ""
          }`}
          onClick={() => setActiveTab("events")}
        >
          <span className={styles.mobileTabText}>Мероприятия</span>
        </button>
        {canViewChats && (
          <button
            type="button"
            className={`${styles.mobileTabButton} ${
              activeTab === "third" ? styles.mobileTabButtonActive : ""
            }`}
            onClick={() => setActiveTab("third")}
          >
            <span className={styles.mobileTabText}>Чаты</span>
          </button>
        )}
      </nav>
    </div>
  );
}

export default Dashboard;
